import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Box, Button, Sheet, Typography, Stack, Radio, RadioGroup, Checkbox, FormControl, FormLabel, Textarea, LinearProgress, Chip, IconButton, useColorScheme, Alert, Card, CardContent, Drawer, Modal, ModalDialog, ModalClose } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutContext } from '../context';
import { APISubmitAnswer, APIGetTestDetail, APIMarkQuestion, APIDeleteQuestion, APIUpdateQuestion, APIResetAnswer, APIFlagQuestion } from '../api';
import TestResultsModal from '../components/modals/TestResultsModal';
import QuestionModal from '../components/modals/QuestionModal';
import ReportModal from '../components/modals/ReportModal';
import DiscussionPanel from '../components/DiscussionPanel';

import { XCircle, Warning, CheckCircle, House, CaretLeft, CaretRight, SkipForward, List, Moon, Sun, BookmarkSimple, Lightning, Trash, Flag, PencilSimple, CaretDoubleLeft, CaretDoubleRight, Translate, Exam, ArrowLeft } from "@phosphor-icons/react";

import useMediaQuery from '@mui/material/useMediaQuery';
import { SafeHTML } from '../utils';
import { theme } from '../App';


/**
 * Подсветить и разложить по абзацам разбор вариантов ответа в объяснении.
 * Источник часто плотный текст без явных абзацев — наш рендер делает:
 * 1. Перед каждым "(Choice X)" / "(Вариант X)" / "X is correct/incorrect" — новый абзац
 * 2. Букву варианта оборачивает в <strong> с акцентным цветом
 * 3. Сохраняет существующие <p>/<br>/<strong>
 *
 * Пишется на лету при рендере — никаких изменений в БД.
 */
function formatExplanationHtml(html) {
    if (!html || typeof html !== 'string') return html;

    const hasParagraphs = /<p[\s>]/i.test(html);
    const GREEN = '#0F7B6C';   // correct answer — успех
    const ORANGE = '#D97706';  // distractors / incorrect — внимание

    // Паттерны:
    //   - correct: явно "X is correct" (зелёный)
    //   - incorrect/distractor: "(Choice X)", "X is incorrect", "X." в начале (оранжевый)
    const correctPatterns = [
        /\b([A-E]\s+is\s+correct[:.]?)/gi,
    ];
    const incorrectPatterns = [
        /(\(Choice\s+[A-E]\))/g,
        /(\(Вариант\s+[А-Е]\))/g,
        /(\(Ответ\s+[А-Е]\))/g,
        /\b([A-E]\s+is\s+incorrect[:.]?)/gi,
        /(?:^|\.\s+|>)\s*([A-E][\.\)]\s)/g,
    ];

    const wrap = (label, color) =>
        `<strong style="color:${color};font-weight:700">${label.trim()}</strong> `;

    let formatted = html;
    const paragraphPrefix = hasParagraphs ? '<br><br>' : '\n\n';

    correctPatterns.forEach(pattern => {
        formatted = formatted.replace(pattern, (m, label) => paragraphPrefix + wrap(label, GREEN));
    });
    incorrectPatterns.forEach(pattern => {
        formatted = formatted.replace(pattern, (m, label) => paragraphPrefix + wrap(label, ORANGE));
    });

    if (!hasParagraphs) {
        const paras = formatted.split(/\n\n+/).map(p => p.trim()).filter(Boolean);
        formatted = paras.map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
    }

    return formatted;
}


export default function TestSessionPage() {
    const { testId } = useParams();
    const navigate = useNavigate();
    const { token, user } = useContext(LayoutContext);
    const isAdmin = user?.role === 'admin';
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [test, setTest] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState([]);
    const [textAnswer, setTextAnswer] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [marking, setMarking] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showResult, setShowResult] = useState(false);
    const [submissionResult, setSubmissionResult] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    
    const [instantAnswerMode, setInstantAnswerMode] = useState(false);
    const [instantAnswerResult, setInstantAnswerResult] = useState(null);

    // Flagged questions (stored in localStorage)
    const [flaggedQuestions, setFlaggedQuestions] = useState(() => {
        const saved = localStorage.getItem('flaggedQuestions');
        return saved ? JSON.parse(saved) : [];
    });

    // Состояние для модального окна результатов
    const [resultsModalOpen, setResultsModalOpen] = useState(false);

    // Состояние для модального окна редактирования вопроса (админ)
    const [editModalOpen, setEditModalOpen] = useState(false);

    // Состояние для админского флага "требует проверки"
    const [reviewFlagOpen, setReviewFlagOpen] = useState(false);
    const [reviewNote, setReviewNote] = useState('');
    const [reviewFlagging, setReviewFlagging] = useState(false);

    // Состояние для модального окна жалобы
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [xpGained, setXpGained] = useState(null);
    const [levelUp, setLevelUp] = useState(false);
    const [panelHidden, setPanelHidden] = useState(false);

    // Language toggle (EN/RU)
    const [lang, setLang] = useState(() => {
        try { return localStorage.getItem('testLang') || 'en'; }
        catch { return 'en'; }
    });
    const toggleLang = () => {
        const next = lang === 'en' ? 'ru' : 'en';
        setLang(next);
        try { localStorage.setItem('testLang', next); } catch {}
    };
    // Helper: pick RU or EN text
    const t = (en, ru) => lang === 'ru' && ru ? ru : en;

    // Tour banner: show if user is mid-tour (detected via localStorage).
    const [tourBannerVisible] = useState(() => {
        try {
            return localStorage.getItem('apex_tour_pending') === '1'
                && localStorage.getItem('apex_tour_done') !== '1';
        } catch { return false; }
    });

    const returnToPlan = () => {
        navigate('/plans');
    };

    useEffect(() => {
        setLoading(true);
        fetchTestData();
    }, [testId]);

    // Pre-populate answered state for the current question whenever test loads
    // or the current index changes. Without this, the first question of a returning
    // user appears unanswered even if user_answer exists in fetched data.
    useEffect(() => {
        if (!test?.test_questions) return;
        const targetQuestion = test.test_questions[currentQuestionIndex];
        if (!targetQuestion) return;
        if (targetQuestion.is_answered && !targetQuestion.is_skipped && targetQuestion.user_answer) {
            const previousAnswers = targetQuestion.user_answer.selected_answers?.map(a => a.id) || [];
            setSelectedAnswers(previousAnswers);
            setShowResult(true);
            setSubmissionResult({
                is_correct: targetQuestion.user_answer.is_correct,
                explanation: targetQuestion.question?.explanation,
                explanation_ru: targetQuestion.question?.explanation_ru
            });
        }
    }, [test, currentQuestionIndex]);

    // Навигация стрелками клавиатуры
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Игнорируем если фокус в текстовом поле
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            if (!test?.test_questions) return;

            if (e.key === 'ArrowLeft' && currentQuestionIndex > 0) {
                handleQuestionNavigation(currentQuestionIndex - 1);
            } else if (e.key === 'ArrowRight' && currentQuestionIndex < test.test_questions.length - 1) {
                handleQuestionNavigation(currentQuestionIndex + 1);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentQuestionIndex, test?.test_questions?.length]);

    const fetchTestData = async () => {
        try {
            // setLoading(true);
            const response = await APIGetTestDetail(token, testId);
            if (response.success) {
                setTest(response.test);
            }
        } catch (error) {
            console.error('Error fetching test:', error);
        } finally {
            setLoading(false);
        }
    };

    const currentQuestion = test?.test_questions?.[currentQuestionIndex];
    const question = currentQuestion?.question;

    // Проверяем, отвечен ли уже вопрос
    const userAnswer = currentQuestion?.user_answer;

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const handleInstantAnswerCheck = async (answerId) => {
        if (!currentQuestion || !instantAnswerMode) return;
        
        const question = currentQuestion.question;
        
        if (question.type !== 'single') {
            // Режим мгновенного ответа работает только для одиночного выбора
            return;
        }
        
        // Находим правильный ответ
        const correctAnswer = question.answers.find(answer => answer.is_correct && answer.is_active);
        const isCorrect = correctAnswer && correctAnswer.id === answerId;
        
        // Устанавливаем результат для отображения
        setInstantAnswerResult({
            isCorrect,
            selectedAnswerId: answerId,
            correctAnswerId: correctAnswer?.id,
            explanation: question.explanation,
            explanation_ru: question.explanation_ru,
            correctAnswerDescription: correctAnswer?.description
        });
        
        // Автоматически отправляем ответ на сервер
        try {
            await APISubmitAnswer(token, {
                test_question_id: currentQuestion.id,
                selected_answer_ids: [answerId],
                text_answer: '',
                is_skipped: false
            });

            // Обновляем данные теста и ждём
            await fetchTestData();
        } catch (error) {
            console.error('Error submitting instant answer:', error);
        }
    };

    const handleAnswerSelect = (answerId) => {
        if (question?.type === 'single') {
            setSelectedAnswers([answerId]);
        } else if (question?.type === 'multiple') {
            setSelectedAnswers(prev => 
                prev.includes(answerId) 
                    ? prev.filter(id => id !== answerId)
                    : [...prev, answerId]
            );
        }
    };
    
    // Функция для маркировки вопроса
    const handleMarkQuestion = async () => {
        if (!currentQuestion) return;
        
        setMarking(true);
        try {
            const response = await APIMarkQuestion(token, {
                test_question_id: currentQuestion.id,
                is_marked: !currentQuestion.is_marked
            });
            
            if (response.success) {
                // Обновляем данные теста
                fetchTestData();
            }
        } catch (error) {
            console.error('Error marking question:', error);
        } finally {
            setMarking(false);
        }
    };

    // Функция для удаления вопроса из базы данных
    const handleDeleteQuestion = async () => {
        if (!currentQuestion || !question) return;

        const confirmDelete = window.confirm(
            `Удалить вопрос "${question.text.substring(0, 50)}..." из базы данных? Это действие нельзя отменить.`
        );

        if (!confirmDelete) return;

        setDeleting(true);
        try {
            const response = await APIDeleteQuestion(token, question.id);

            if (response.success) {
                // Переходим к следующему вопросу или предыдущему
                if (currentQuestionIndex < test.test_questions.length - 1) {
                    setCurrentQuestionIndex(prev => prev);
                } else if (currentQuestionIndex > 0) {
                    setCurrentQuestionIndex(prev => prev - 1);
                }
                // Обновляем данные теста
                fetchTestData();
            }
        } catch (error) {
            console.error('Error deleting question:', error);
            alert('Ошибка при удалении вопроса');
        } finally {
            setDeleting(false);
        }
    };

    // Флажок "плохой вопрос" - сохраняем в localStorage
    const handleFlagQuestion = () => {
        if (!question) return;

        const questionId = question.id;
        let newFlagged;

        if (flaggedQuestions.includes(questionId)) {
            newFlagged = flaggedQuestions.filter(id => id !== questionId);
        } else {
            newFlagged = [...flaggedQuestions, questionId];
        }

        setFlaggedQuestions(newFlagged);
        localStorage.setItem('flaggedQuestions', JSON.stringify(newFlagged));
    };

    // Админский флаг "требует проверки" — сохранить с заметкой
    const handleReviewFlagSave = async () => {
        if (!question) return;
        setReviewFlagging(true);
        try {
            const response = await APIFlagQuestion(token, question.id, reviewNote, true);
            if (response.success) {
                // Обновим локально
                if (question) {
                    question.needs_review = true;
                    question.review_note = reviewNote;
                }
                setReviewFlagOpen(false);
                setReviewNote('');
            }
        } catch (e) {
            alert('Ошибка при пометке вопроса');
        } finally {
            setReviewFlagging(false);
        }
    };

    // Снять админский флаг "требует проверки"
    const handleReviewFlagRemove = async () => {
        if (!question) return;
        setReviewFlagging(true);
        try {
            const response = await APIFlagQuestion(token, question.id, '', false);
            if (response.success) {
                if (question) {
                    question.needs_review = false;
                    question.review_note = '';
                }
                setReviewFlagOpen(false);
                setReviewNote('');
            }
        } catch (e) {
            alert('Ошибка при снятии флага');
        } finally {
            setReviewFlagging(false);
        }
    };

    // Открыть модалку флажка (с существующей заметкой если есть)
    const openReviewFlag = () => {
        setReviewNote(question?.review_note || '');
        setReviewFlagOpen(true);
    };

    // Сохранение отредактированного вопроса (админ)
    const handleSaveQuestion = async (formData) => {
        if (!question) return;

        try {
            const response = await APIUpdateQuestion(token, question.id, formData);
            if (response.success !== false) {
                // Обновляем данные теста
                await fetchTestData();
                setEditModalOpen(false);
            } else {
                alert('Ошибка при сохранении: ' + (response.error || 'Неизвестная ошибка'));
            }
        } catch (error) {
            console.error('Error saving question:', error);
            alert('Ошибка при сохранении вопроса');
        }
    };

    const isQuestionFlagged = question && flaggedQuestions.includes(question.id);

    const handleSubmitAnswer = async (isSkipped = false) => {
        if (!currentQuestion) return;
        // Prevent double submission
        if (submitting) return;

        setSubmitting(true);
        try {
            const response = await APISubmitAnswer(token, {
                test_question_id: currentQuestion.id,
                selected_answer_ids: isSkipped ? [] : selectedAnswers,
                text_answer: isSkipped ? '' : textAnswer,
                is_skipped: isSkipped
            });

            if (response.success) {
                setSubmissionResult(response);

                // XP toast
                if (response.xp_earned) {
                    setXpGained(response.xp_earned);
                    setLevelUp(response.level_up || false);
                    setTimeout(() => setXpGained(null), 2000);
                }

                // Обновляем данные теста и ждём завершения
                await fetchTestData();

                // В учебном режиме показываем результат
                if (test.mode === 'study' && !isSkipped) {
                    setShowResult(true);
                } else {
                    // В тестовом режиме или при пропуске сразу переходим дальше
                    moveToNextQuestion(response);
                }
            } else {
                console.error('Submit answer failed:', response.error);
            }
        } catch (error) {
            console.error('Error submitting answer:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const moveToNextQuestion = (response) => {
        // Сбрасываем состояние ответов
        setSelectedAnswers([]);
        setTextAnswer('');
        setShowResult(false);
        setSubmissionResult(null);
        setInstantAnswerResult(null);

        // Переходим к следующему вопросу или показываем результаты
        if (response?.test_completed || isLastQuestion) {
            setResultsModalOpen(true);
        } else if (currentQuestionIndex < test.test_questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const handleContinue = async () => {
        // Сбрасываем состояние ответов
        setSelectedAnswers([]);
        setTextAnswer('');
        setShowResult(false);
        setSubmissionResult(null);
        setInstantAnswerResult(null);

        // Проверяем, последний ли вопрос
        if (isLastQuestion) {
            // Показываем модальное окно с результатами
            setResultsModalOpen(true);
        } else {
            // Переходим к следующему вопросу
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const handleQuestionNavigation = (index) => {
        const targetQuestion = test?.test_questions?.[index];

        setCurrentQuestionIndex(index);
        setInstantAnswerResult(null);

        // Если вопрос был отвечен (не пропущен) — восстанавливаем состояние
        if (targetQuestion?.is_answered && !targetQuestion?.is_skipped && targetQuestion?.user_answer) {
            const previousAnswers = targetQuestion.user_answer.selected_answers?.map(a => a.id) || [];
            setSelectedAnswers(previousAnswers);
            setShowResult(true);
            setSubmissionResult({
                is_correct: targetQuestion.user_answer.is_correct,
                explanation: targetQuestion.question?.explanation,
                explanation_ru: targetQuestion.question?.explanation_ru
            });
        } else {
            // Пропущенные или неотвеченные — чистое состояние
            setSelectedAnswers([]);
            setTextAnswer('');
            setShowResult(false);
            setSubmissionResult(null);
        }

        if (isMobile) {
            setSidebarOpen(false);
        }
    };

    const handleResetAnswer = () => {
        setSelectedAnswers([]);
    };

    // Рендер вариантов ответов в зависимости от типа вопроса
    const renderAnswerOptions = () => {
        if (!question) return null;

        const activeAnswers = question.answers.filter(answer => answer.is_active);
        const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
        
        switch (question.type) {
            case 'single':
                return (
                    <>
                        <RadioGroup
                            value={selectedAnswers[0] || ''}
                            onChange={(e) => {
                                const answerId = parseInt(e.target.value);
                                handleAnswerSelect(answerId);
                                
                                // Если включен режим мгновенного ответа, сразу проверяем
                                if (instantAnswerMode) {
                                    handleInstantAnswerCheck(answerId);
                                }
                            }}
                            sx={{ gap: 1 }}
                        >
                            {activeAnswers.map((answer, index) => (
                                <AnswerOption
                                    key={answer.id}
                                    answer={answer}
                                    type="radio"
                                    selected={selectedAnswers.includes(answer.id)}
                                    showResult={showResult || instantAnswerResult !== null}
                                    userAnswer={userAnswer}
                                    isCorrect={submissionResult?.is_correct}
                                    letter={letters[index]}
                                    instantAnswerMode={instantAnswerMode}
                                    instantAnswerResult={instantAnswerResult}
                                    onInstantAnswerCheck={handleInstantAnswerCheck}
                                    t={t}
                                />
                            ))}
                        </RadioGroup>
                        
                        {/* Кнопка сброса ответа для одиночного выбора */}
                        {selectedAnswers.length > 0 && !showResult && !instantAnswerResult && (
                            <Button
                                variant="plain"
                                color="danger"
                                size="sm"
                                onClick={handleResetAnswer}
                                sx={{ ml: 'auto', mt: 1, alignSelf: 'flex-start' }}
                            >
                                Сбросить ответ
                            </Button>
                        )}
                    </>
                );

            case 'multiple':
                return (
                    <Stack spacing={1}>
                        {activeAnswers.map((answer, index) => (
                            <AnswerOption
                                key={answer.id}
                                answer={answer}
                                type="checkbox"
                                selected={selectedAnswers.includes(answer.id)}
                                onChange={() => handleAnswerSelect(answer.id)}
                                showResult={showResult}
                                userAnswer={userAnswer}
                                isCorrect={submissionResult?.is_correct}
                                letter={letters[index]}
                                t={t}
                            />
                        ))}
                    </Stack>
                );

            case 'text':
                return (
                    <Textarea
                        value={textAnswer}
                        onChange={(e) => setTextAnswer(e.target.value)}
                        minRows={4}
                        placeholder="Введите ваш ответ здесь..."
                        disabled={showResult}
                    />
                );

            default:
                return null;
        }
    };
    
    // В основном компоненте, после renderAnswerOptions(), добавим:
    const renderInstantAnswerResult = () => {
        // Если есть submissionResult (учебный режим — рендерится через renderAnswerResult
        // с разбивкой по A/B/C/D), instant-фоллбэк не показываем чтобы не было дубля.
        if (!instantAnswerResult || !currentQuestion || submissionResult) return null;
        
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Alert
                    variant="outlined"
                    startDecorator={
                        instantAnswerResult.isCorrect ?
                        <CheckCircle size={20} weight="fill" color="#2dd4a8" /> :
                        <XCircle size={20} weight="fill" color="#e07070" />
                    }
                    sx={{
                        mb: 2,
                        borderLeft: '4px solid',
                        borderLeftColor: instantAnswerResult.isCorrect
                            ? (theme) => theme.palette.mode === 'dark' ? '#2dd4a8' : '#0F7B6C'
                            : (theme) => theme.palette.mode === 'dark' ? '#e07070' : '#a83232',
                        bgcolor: instantAnswerResult.isCorrect
                            ? (theme) => theme.palette.mode === 'dark' ? 'rgba(45, 212, 168, 0.08)' : 'rgba(15, 123, 108, 0.05)'
                            : (theme) => theme.palette.mode === 'dark' ? 'rgba(190, 70, 70, 0.1)' : 'rgba(168, 50, 50, 0.05)',
                    }}
                >
                    <Typography level="body1" fontWeight={600} sx={{
                        color: instantAnswerResult.isCorrect
                            ? (theme) => theme.palette.mode === 'dark' ? '#2dd4a8' : '#0F7B6C'
                            : (theme) => theme.palette.mode === 'dark' ? '#e07070' : '#a83232',
                    }}>
                        {instantAnswerResult.isCorrect ? 'Правильно!' : 'Неправильно!'}
                    </Typography>
                </Alert>

                {/* Объяснение вопроса */}
                {(instantAnswerResult.explanation || instantAnswerResult.correctAnswerDescription) && (
                    <Card variant="soft" color="neutral" sx={{ mt: 2 }}>
                        <CardContent>
                            <Typography level="title-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Объяснение:
                            </Typography>
                            {instantAnswerResult.explanation && (
                                <SafeHTML
                                    html={t(instantAnswerResult.explanation, instantAnswerResult.explanation_ru)}
                                    sx={{ color: 'text.primary' }}
                                />
                            )}
                            {instantAnswerResult.correctAnswerDescription && (
                                <SafeHTML
                                    html={instantAnswerResult.correctAnswerDescription}
                                    sx={{ color: 'text.secondary', mt: 1 }}
                                />
                            )}
                        </CardContent>
                    </Card>
                )}
            </motion.div>
        );
    };

    // Рендер результата ответа (для учебного режима)
    const renderAnswerResult = () => {
        if (!showResult || !submissionResult) return null;

        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Alert
                    variant="outlined"
                    startDecorator={
                        submissionResult.is_correct ?
                        <CheckCircle size={20} weight="fill" color="#2dd4a8" /> :
                        <XCircle size={20} weight="fill" color="#e07070" />
                    }
                    sx={{
                        mb: 2,
                        borderLeft: '4px solid',
                        borderLeftColor: submissionResult.is_correct
                            ? (theme) => theme.palette.mode === 'dark' ? '#2dd4a8' : '#0F7B6C'
                            : (theme) => theme.palette.mode === 'dark' ? '#e07070' : '#a83232',
                        bgcolor: submissionResult.is_correct
                            ? (theme) => theme.palette.mode === 'dark' ? 'rgba(45, 212, 168, 0.08)' : 'rgba(15, 123, 108, 0.05)'
                            : (theme) => theme.palette.mode === 'dark' ? 'rgba(190, 70, 70, 0.1)' : 'rgba(168, 50, 50, 0.05)',
                    }}
                >
                    <Typography level="body1" fontWeight={600} sx={{
                        color: submissionResult.is_correct
                            ? (theme) => theme.palette.mode === 'dark' ? '#2dd4a8' : '#0F7B6C'
                            : (theme) => theme.palette.mode === 'dark' ? '#e07070' : '#a83232',
                    }}>
                        {submissionResult.is_correct ? 'Правильно!' : 'Неправильно!'}
                    </Typography>
                </Alert>

                {/* Объяснение — UWorld-style: нейтральный контейнер,
                    pill-label «Объяснение» сверху, крупный читаемый текст.
                    Buttons вариантов внутри: green = correct, orange = incorrect. */}
                {submissionResult.explanation && (
                    <Box sx={{
                        mt: 2,
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '10px',
                        p: { xs: 2, sm: 3 },
                        pt: { xs: 1.5, sm: 2 },
                    }}>
                        {/* Pill-label */}
                        <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            bgcolor: 'background.surface',
                            px: 1.75,
                            py: 0.5,
                            borderRadius: '6px',
                            mb: 2.5,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}>
                            <Typography sx={{
                                fontWeight: 700,
                                fontSize: '0.82rem',
                                color: 'text.primary',
                                letterSpacing: '0.01em',
                            }}>
                                Объяснение
                            </Typography>
                        </Box>

                        {/* Body — формат: жирные буквенные метки + воздух между абзацами */}
                        <SafeHTML
                            html={formatExplanationHtml(t(submissionResult.explanation, submissionResult.explanation_ru))}
                            sx={{
                                fontSize: { xs: '0.95rem', sm: '1.05rem' },
                                lineHeight: 1.75,
                                color: 'text.primary',
                                '& p': { mb: 2.5 },
                                '& p:last-child': { mb: 0 },
                                '& strong, & b': { fontWeight: 700, color: 'text.primary' },
                                '& ul, & ol': { ml: 2.5, mb: 2 },
                                '& li': { mb: 0.5, lineHeight: 1.65 },
                                '& h1, & h2, & h3, & h4': { mt: 2, mb: 1, fontWeight: 700 },
                                '& br + br': { lineHeight: 1.2 },
                            }}
                        />
                    </Box>
                )}
            </motion.div>
        );
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography level="h4">Загрузка теста...</Typography>
            </Box>
        );
    }

    if (!test) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography level="h4">Тест не найден</Typography>
            </Box>
        );
    }

    const progress = ((currentQuestionIndex + 1) / test.total_questions) * 100;

    const isLastQuestion = currentQuestionIndex === test?.test_questions?.length - 1;
    const shouldShowContinueButton = showResult || instantAnswerResult !== null;
    const isAnswerSelected = selectedAnswers.length > 0 || (question?.type === 'text' && textAnswer.trim());

    let mainButtonText = 'Ответить';
    let mainButtonAction = () => handleSubmitAnswer(false);
    let mainButtonDisabled = submitting || !isAnswerSelected;

    if (shouldShowContinueButton) {
        mainButtonText = isLastQuestion ? 'Завершить тест' : 'Следующий вопрос';
        mainButtonAction = handleContinue;
        mainButtonDisabled = false;
    } else if (instantAnswerMode && isAnswerSelected) {
        // В режиме мгновенного ответа кнопка становится "Следующий вопрос" после выбора ответа
        mainButtonText = isLastQuestion ? 'Завершить тест' : 'Следующий вопрос';
        mainButtonAction = handleContinue;
        mainButtonDisabled = false;
    }

    return (
        <Box sx={{ display: 'flex', height: '100dvh' }}>
            {/* Боковая панель навигации */}
            {!isMobile ? (
                // Десктоп версия
                !panelHidden && (
                    <TestNavigationSidebar
                        test={test}
                        currentQuestionIndex={currentQuestionIndex}
                        onQuestionSelect={handleQuestionNavigation}
                        showResult={showResult}
                        instantAnswerMode={instantAnswerMode}
                        setInstantAnswerMode={setInstantAnswerMode}
                    />
                )
            ) : (
                // Мобильная версия - Drawer
                <Drawer
                    open={sidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: 280,
                            boxSizing: 'border-box',
                        },
                    }}
                >
                    <TestNavigationSidebar
                        test={test}
                        currentQuestionIndex={currentQuestionIndex}
                        onQuestionSelect={handleQuestionNavigation}
                        showResult={showResult}
                        instantAnswerMode={instantAnswerMode}
                        setInstantAnswerMode={setInstantAnswerMode}
                    />
                </Drawer>
            )}

            {/* Основной контент */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Хедер теста */}
                <Sheet variant="soft" sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <IconButton
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            onClick={isMobile ? toggleSidebar : () => setPanelHidden(!panelHidden)}
                            title={isMobile ? (sidebarOpen ? 'Скрыть панель' : 'Показать панель') : (panelHidden ? 'Показать панель' : 'Скрыть панель')}
                        >
                            {(isMobile ? sidebarOpen : !panelHidden) ? <CaretDoubleLeft size={18} /> : <CaretDoubleRight size={18} />}
                        </IconButton>
                        <Typography level="h4" sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'start' }}>{test.title}</Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Chip variant="soft" color="primary" size="sm">
                                {currentQuestionIndex + 1} / {test.total_questions}
                            </Chip>
                            <Chip variant="outlined" size="sm" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                                {test.mode === 'study' ? 'Учебный режим' : 'Тестовый режим'}
                            </Chip>
                            {/* Language toggle EN/RU */}
                            <Button
                                variant={lang === 'ru' ? 'solid' : 'outlined'}
                                color="neutral"
                                size="sm"
                                onClick={toggleLang}
                                startDecorator={<Translate size={14} />}
                                sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    px: 1,
                                    py: 0.2,
                                    minHeight: 0,
                                    ...(lang === 'ru' && {
                                        bgcolor: 'primary.solidBg',
                                        color: 'primary.solidColor',
                                    }),
                                }}
                            >
                                {lang === 'ru' ? 'RU' : 'EN'}
                            </Button>
                            {/* Иконки: жалоба, закладка, редактирование */}
                            <IconButton
                                variant="plain"
                                color="warning"
                                size="sm"
                                onClick={() => setReportModalOpen(true)}
                                title="Пожаловаться на вопрос"
                            >
                                <Warning size={18} />
                            </IconButton>
                            <IconButton
                                variant="plain"
                                color={currentQuestion?.is_marked ? "primary" : "neutral"}
                                size="sm"
                                onClick={handleMarkQuestion}
                                loading={marking}
                                disabled={marking}
                                title="Сохранить вопрос"
                            >
                                {currentQuestion?.is_marked ? <BookmarkSimple size={18} weight="fill" /> : <BookmarkSimple size={18} />}
                            </IconButton>
                            {isAdmin && (
                                <IconButton
                                    variant="plain"
                                    color="neutral"
                                    size="sm"
                                    onClick={() => setEditModalOpen(true)}
                                    title="Редактировать вопрос"
                                >
                                    <PencilSimple size={18} />
                                </IconButton>
                            )}
                            {isAdmin && (
                                <IconButton
                                    variant="plain"
                                    color="danger"
                                    size="sm"
                                    onClick={handleDeleteQuestion}
                                    loading={deleting}
                                    disabled={deleting}
                                    title="Удалить вопрос из базы"
                                >
                                    <Trash size={18} />
                                </IconButton>
                            )}
                            {isAdmin && (
                                <IconButton
                                    variant={question?.needs_review ? 'soft' : 'plain'}
                                    color={question?.needs_review ? 'warning' : 'neutral'}
                                    size="sm"
                                    onClick={openReviewFlag}
                                    title={question?.needs_review ? 'Помечен для проверки' : 'Пометить для проверки'}
                                >
                                    <Flag size={18} weight={question?.needs_review ? 'fill' : 'regular'} />
                                </IconButton>
                            )}
                            {/* APEX MED branding */}
                            <Typography
                                level="body-xs"
                                sx={{
                                    color: '#DA7756',
                                    fontWeight: 700,
                                    letterSpacing: '0.12em',
                                    border: '1.5px solid #DA7756',
                                    borderRadius: 'sm',
                                    px: 1.5,
                                    py: 0.3,
                                    fontSize: '0.65rem',
                                    display: { xs: 'none', md: 'block' },
                                }}
                            >
                                APEX MED
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* Мобилка: APEX MED вместо названия теста */}
                    <Typography
                        level="body-sm"
                        sx={{
                            mt: 1,
                            display: { xs: 'block', sm: 'none' },
                            textAlign: 'start',
                            color: '#DA7756',
                            fontWeight: 700,
                            letterSpacing: '0.12em',
                            fontSize: '0.75rem',
                        }}
                    >
                        APEX MED
                    </Typography>
                </Sheet>

                {/* Tour banner — sticky, impossible to miss */}
                {tourBannerVisible && (
                    <Sheet
                        variant="soft"
                        sx={{
                            bgcolor: '#DA7756',
                            px: { xs: 2, sm: 3 },
                            py: { xs: 1.5, sm: 1.75 },
                            boxShadow: '0 4px 20px rgba(184,76,49,0.35)',
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            spacing={1.5}
                        >
                            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flex: 1 }}>
                                <Exam size={24} color="#FFFFFF" weight="duotone" />
                                <Box>
                                    <Typography
                                        level="body-sm"
                                        sx={{ color: '#FFFFFF', fontWeight: 700, lineHeight: 1.2 }}
                                    >
                                        Ты в туре — реши и вернись
                                    </Typography>
                                    <Typography
                                        level="body-xs"
                                        sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25 }}
                                    >
                                        Пройди тест. Потом нажми кнопку — тур продолжится.
                                    </Typography>
                                </Box>
                            </Stack>
                            <Button
                                variant="solid"
                                size="md"
                                onClick={returnToPlan}
                                startDecorator={<ArrowLeft size={16} />}
                                sx={{
                                    bgcolor: '#FFFFFF',
                                    color: '#B84C31',
                                    fontWeight: 700,
                                    px: 3,
                                    width: { xs: '100%', sm: 'auto' },
                                    '&:hover': { bgcolor: '#FFF8F0' },
                                }}
                            >
                                Назад к плану
                            </Button>
                        </Stack>
                    </Sheet>
                )}

                {/* Область вопроса */}
                <Box sx={{ flex: 1, p: { xs: 1.5, sm: 2.5 }, pt: { xs: 1, sm: 1.5 }, overflowY: 'auto', overflowX: 'hidden' }}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentQuestion?.id}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -50 }}
                            transition={{ duration: 0.3 }}
                        >
                            {question && (
                                <Stack spacing={3}>
                                    {/* Текст вопроса (+ клинический случай внутри) */}
                                    <Sheet variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 'lg' }}>
                                        {/* Клинический случай */}
                                        {question.clinical_case_text && (
                                            <Box sx={{
                                                mb: 2.5,
                                                pb: 2.5,
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                            }}>
                                                <Typography level="h4" sx={{ fontWeight: 400, lineHeight: 1.7, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                                                    <SafeHTML
                                                        html={t(question.clinical_case_text, question.clinical_case_text_ru)}
                                                        sx={{
                                                            '& p': { mb: 0.5 },
                                                        }}
                                                    />
                                                </Typography>
                                            </Box>
                                        )}

                                        {question.description && (
                                            <SafeHTML
                                                html={t(question.description, question.description_ru)}
                                                sx={{
                                                    mt: 0,
                                                    '& p': { mb: 1 },
                                                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                                                        mt: 0, mb: 1,
                                                        color: 'text.primary'
                                                    },
                                                    '& blockquote': {
                                                        borderLeft: '4px solid',
                                                        borderColor: 'primary.300',
                                                        pl: 2,
                                                        ml: 0,
                                                        fontStyle: 'italic',
                                                        color: 'text.secondary'
                                                    }
                                                }}
                                            />
                                        )}

                                        {/* Текст вопроса */}
                                        <Typography level="h4" sx={{ mt: (question.description || question.clinical_case_text) ? 2 : 0, fontSize: { xs: '1.05rem', sm: '1.2rem' } }}>
                                            {t(question.text, question.text_ru)}
                                        </Typography>
                                    </Sheet>


                                    {/* Варианты ответов */}
                                    <FormControl>
                                        <FormLabel>
                                            {question.type === 'single'
                                                ? 'Выберите один вариант:'
                                                : question.type === 'multiple'
                                                ? 'Выберите один или несколько вариантов:'
                                                : 'Введите ваш ответ:'}
                                        </FormLabel>
                                        {renderAnswerOptions()}
                                    </FormControl>

                                    {/* Текстовый ответ */}
                                    {question.type === 'text' && (
                                        <FormControl>
                                            <FormLabel>Введите ваш ответ:</FormLabel>
                                            <Textarea
                                                value={textAnswer}
                                                onChange={(e) => setTextAnswer(e.target.value)}
                                                minRows={4}
                                                placeholder="Введите ваш ответ здесь..."
                                                sx={{ mt: 1 }}
                                            />
                                        </FormControl>
                                    )}

                                    {/* Результат ответа (для учебного режима) */}
                                    {renderAnswerResult()}

                                    {renderInstantAnswerResult()}

                                    {/* Discussion panel - показываем после ответа */}
                                    {(showResult || instantAnswerResult || currentQuestion?.is_answered) && (
                                        <DiscussionPanel questionId={question?.id} />
                                    )}

                                    {/* Кнопки навигации */}
                                    <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={2} justifyContent="space-between">
                                        
                                        <Stack direction={"column"} spacing={1}>
                                            {/* Основная кнопка */}
                                            <Button
                                                variant="solid"
                                                color="primary"
                                                endDecorator={
                                                    (shouldShowContinueButton || (instantAnswerMode && isAnswerSelected)) ?
                                                    <CaretRight size={16} /> : null
                                                }
                                                onClick={mainButtonAction}
                                                loading={submitting}
                                                disabled={mainButtonDisabled}
                                                sx={{
                                                    width: {
                                                        xs: isMobile ? '100%' : 'auto',
                                                        sm: 'auto'
                                                    }
                                                }}
                                            >
                                                {mainButtonText}
                                            </Button>

                                            {/* Кнопка Назад */}
                                            <Button
                                                variant="outlined"
                                                startDecorator={<CaretLeft size={16} />}
                                                onClick={() => handleQuestionNavigation(Math.max(0, currentQuestionIndex - 1))}
                                                disabled={currentQuestionIndex === 0 || submitting}
                                            >
                                                Назад
                                            </Button>
                                        </Stack>

                                        <Stack direction="row" justifyContent={{ xs: 'space-between', sm: "flex-start" }} alignItems={"center"} spacing={1}>
                                            {/* Сброс ответа (админ) */}
                                            {isAdmin && currentQuestion?.is_answered && !currentQuestion?.is_skipped && (
                                                <Button
                                                    variant="soft"
                                                    color="warning"
                                                    size="sm"
                                                    onClick={async () => {
                                                        try {
                                                            await APIResetAnswer(token, currentQuestion.id);
                                                            setShowResult(false);
                                                            setInstantAnswerResult(null);
                                                            setSelectedAnswers([]);
                                                            setSubmissionResult(null);
                                                            await fetchTestData();
                                                        } catch (e) {
                                                            alert('Ошибка: ' + (e.response?.data?.error || e.message));
                                                        }
                                                    }}
                                                    sx={{ height: 'fit-content' }}
                                                >
                                                    Сбросить ответ
                                                </Button>
                                            )}
                                            {/* Кнопка Пропустить (только когда не показываем результат) */}
                                            {!shouldShowContinueButton && !(instantAnswerMode && isAnswerSelected) && (
                                                <Button
                                                    variant="soft"
                                                    color="neutral"
                                                    endDecorator={<SkipForward size={18} />}
                                                    onClick={() => handleSubmitAnswer(true)}
                                                    loading={submitting}
                                                    disabled={submitting}
                                                    sx={{ height: "fit-content" }}
                                                >
                                                    Пропустить
                                                </Button>
                                            )}
                                        </Stack>
                                        
                                        {/* <Stack direction="row" justifyContent={{ xs: 'space-between', sm: "flex-start" }} alignItems={"center"} spacing={1}>
                                            {!showResult && (
                                                <Button
                                                    variant="soft"
                                                    color="neutral"
                                                    endDecorator={<SkipForward size={18} />}
                                                    onClick={() => handleSubmitAnswer(true)}
                                                    loading={submitting}
                                                    disabled={submitting}
                                                    sx={{ height: "fit-content" }}
                                                >
                                                    Пропустить
                                                </Button>
                                            )}
                                            
                                            {showResult && (
                                                <Button
                                                    variant="solid"
                                                    color="primary"
                                                    endDecorator={<ArrowForwardIosRoundedIcon />}
                                                    onClick={handleContinue}
                                                    sx={{
                                                        width: {
                                                            xs: isMobile ? '100%' : 'auto',
                                                            sm: 'auto'
                                                        }
                                                    }}
                                                >
                                                    {submissionResult?.test_completed 
                                                        ? 'Завершить тест' 
                                                        : 'Следующий вопрос'}
                                                </Button>
                                            )}
                                        </Stack> */}
                                    </Stack>
                                </Stack>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </Box>
            </Box>

            {/* Модальное окно результатов теста */}
            <TestResultsModal
                open={resultsModalOpen}
                test={test}
                token={token}
                lang={lang}
                onClose={() => {
                    setResultsModalOpen(false);
                    navigate('/tests');
                }}
            />

            {/* Модалка редактирования вопроса (админ) */}
            {isAdmin && (
                <QuestionModal
                    open={editModalOpen}
                    question={question}
                    onClose={() => setEditModalOpen(false)}
                    onSave={handleSaveQuestion}
                    token={token}
                />
            )}

            {/* Модалка жалобы на вопрос */}
            <ReportModal
                open={reportModalOpen}
                onClose={() => setReportModalOpen(false)}
                questionId={question?.id}
            />

            {/* Модалка флага "требует проверки" (админ) */}
            {isAdmin && (
                <Modal open={reviewFlagOpen} onClose={() => setReviewFlagOpen(false)}>
                    <ModalDialog sx={{ maxWidth: 480, width: '90vw' }}>
                        <ModalClose />
                        <Typography level="h4" sx={{ mb: 1 }}>
                            <Flag size={20} weight="fill" style={{ color: '#f59e0b', marginRight: 8, verticalAlign: 'middle' }} />
                            {question?.needs_review ? 'Помечен для проверки' : 'Пометить для проверки'}
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                            Опиши что нужно сделать с этим вопросом. Потом вернёшься к нему отдельной сессией.
                        </Typography>
                        <Textarea
                            value={reviewNote}
                            onChange={(e) => setReviewNote(e.target.value)}
                            minRows={4}
                            placeholder='Например: "убрать упоминание PHD132", "правильный ответ C, не B", "проверить патофизу"'
                            sx={{ mb: 2 }}
                        />
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {question?.needs_review && (
                                <Button
                                    variant="plain"
                                    color="danger"
                                    onClick={handleReviewFlagRemove}
                                    loading={reviewFlagging}
                                    disabled={reviewFlagging}
                                >
                                    Снять флаг
                                </Button>
                            )}
                            <Button variant="plain" onClick={() => setReviewFlagOpen(false)}>Отмена</Button>
                            <Button
                                color="warning"
                                onClick={handleReviewFlagSave}
                                loading={reviewFlagging}
                                disabled={reviewFlagging || !reviewNote.trim()}
                            >
                                Сохранить
                            </Button>
                        </Stack>
                    </ModalDialog>
                </Modal>
            )}

            {/* XP Toast */}
            {xpGained && (
                <Box sx={{
                    position: 'fixed', bottom: 80, right: 24,
                    bgcolor: levelUp ? 'warning.softBg' : 'success.softBg',
                    border: '1px solid',
                    borderColor: levelUp ? 'warning.outlinedBorder' : 'success.outlinedBorder',
                    borderRadius: '20px', px: 2, py: 0.5,
                    zIndex: 9999,
                    animation: 'fadeInUp 0.3s ease',
                    '@keyframes fadeInUp': {
                        from: { opacity: 0, transform: 'translateY(10px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                    },
                }}>
                    <Typography level="body-sm" fontWeight={600}>
                        {levelUp ? '🎉 Level Up! ' : ''}+{xpGained} XP
                    </Typography>
                </Box>
            )}
        </Box>
    );
}


// Компонент для отображения варианта ответа
function AnswerOption({ answer, type, selected, onChange, showResult, userAnswer, isCorrect, letter, instantAnswerMode = false, instantAnswerResult = null, onInstantAnswerCheck, t = (en) => en }) {
    const isUserSelected = userAnswer?.selected_answers?.some(sa => sa.id === answer.id);
    const isCorrectAnswer = answer.is_correct;
    
    // Определяем, показывать ли мгновенный результат
    const showInstantResult = instantAnswerMode && instantAnswerResult !== null;
    const isInstantSelected = showInstantResult && instantAnswerResult.selectedAnswerId === answer.id;
    const isInstantCorrect = showInstantResult && instantAnswerResult.correctAnswerId === answer.id;

    // Определяем стили в зависимости от состояния
    let variant = 'outlined';
    let color = 'neutral';
    let resultSx = {};

    if (showResult || showInstantResult) {
        if (isCorrectAnswer) {
            // Правильный ответ — vivid teal border + noticeable tint
            resultSx = {
                border: '1.5px solid',
                borderColor: (theme) => theme.palette.mode === 'dark' ? '#2dd4a8' : '#0F7B6C',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(45, 212, 168, 0.14)' : 'rgba(15, 123, 108, 0.08)',
            };
        } else if (isUserSelected || isInstantSelected) {
            // Выбранный неправильный — refined crimson border + subtle tint
            resultSx = {
                border: '1.5px solid',
                borderColor: (theme) => theme.palette.mode === 'dark' ? '#e07070' : '#a83232',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(190, 70, 70, 0.14)' : 'rgba(168, 50, 50, 0.06)',
            };
        } else {
            // Остальные — приглушены
            resultSx = { opacity: 0.4 };
        }
    } else if (selected) {
        // Выбранный ответ (до отправки)
        variant = 'soft';
        color = 'primary';
    }

    const handleClick = () => {
        if (showResult || instantAnswerMode) {
            // В режиме показа результата или мгновенного ответа - не обрабатываем клик
            return;
        }
        
        if (onChange) {
            onChange();
        }
        
        // Если включен режим мгновенного ответа и это одиночный выбор
        if (instantAnswerMode && type === 'radio') {
            onInstantAnswerCheck?.(answer.id);
        }
    };

    return (
        <Card 
            variant={variant}
            color={color}
            component="label"
            sx={{
                p: 2,
                cursor: (showResult || showInstantResult) ? 'default' : 'pointer',
                transition: 'all 0.25s ease',
                ...resultSx,
                '&:hover': !showResult && !showInstantResult ? {
                    backgroundColor: 'background.level1',
                    borderColor: 'primary.300'
                } : {},
            }}
            onClick={handleClick}
        >
            <Stack direction="row" spacing={2} alignItems="center">
                {/* Буква варианта ответа */}
                {type === 'radio' ? (
                    <Radio 
                        value={answer.id}
                        checked={selected}
                        disabled={showResult || showInstantResult}
                        sx={{ mt: 0.25 }}
                    />
                ) : (
                    <Checkbox 
                        checked={selected}
                        onChange={onChange}
                        disabled={showResult || showInstantResult}
                        sx={{ mt: 0.25 }}
                    />
                )}
                
                <Box sx={{ flex: 1 }} onClick={handleClick}>
                    <Stack direction={'row'} spacing={1} alignItems="center">
                        <Box sx={{ 
                            borderRadius: 'sm', 
                            // backgroundColor: selected ? 'primary.500' : 'neutral.200',
                            color: selected ? showResult && isCorrectAnswer ? (theme) => theme.palette.mode === 'dark' ? '#2dd4a8' : '#0F7B6C' : '#DA7756' : 'text.primary',
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            fontSize: 'lg',
                            fontWeight: 'bold',
                            flexShrink: 0
                        }}>
                            {letter}.
                        </Box>
                        
                        <Typography level="body1" sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                            {t(answer.text, answer.text_ru)}
                        </Typography>
                    </Stack>
                    
                    {/* Описание ответа (показываем только для правильных ответов после отправки) */}
                    {showResult && isCorrectAnswer && answer.description && (
                        <SafeHTML 
                            html={answer.description}
                            sx={{ 
                                mt: 1,
                                fontSize: 'sm',
                                color: 'text.secondary',
                                '& p': { mb: 0.5 }
                            }}
                        />
                    )}
                </Box>

                {/* Иконки статуса в режиме показа результата */}
                {(showResult || showInstantResult) && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isCorrectAnswer ? (
                            <CheckCircle size={22} weight="fill" style={{ color: 'var(--joy-palette-mode) === "dark" ? "#2dd4a8" : "#0F7B6C"' }} color="#2dd4a8" />
                        ) : (isUserSelected || isInstantSelected) ? (
                            <XCircle size={20} weight="fill" color="#e07070" />
                        ) : (
                            <XCircle size={18} weight="regular" color="var(--joy-palette-neutral-500, #71717a)" />
                        )}
                    </Box>
                )}
            </Stack>
        </Card>
    );
}



// Компонент Sidebar для навигации по вопросам
function TestNavigationSidebar({ test, currentQuestionIndex, onQuestionSelect, showResult, instantAnswerMode, setInstantAnswerMode }) {
    const { mode, setMode } = useColorScheme();
    const navigate = useNavigate();

    const handleHomeTest = () => {
        navigate(`/tests/`);
    };

    // Функция для переключения темы
    const toggleTheme = () => {
        if (mode === 'light') {
            setMode('dark');
        } else {
            setMode('light');
        }
    };

    return (
        <Sheet 
            variant="outlined" 
            sx={{ 
                width: 300, 
                borderLeft: '1px solid', 
                borderColor: 'divider',
                p: 2,
                overflow: 'auto'
            }}
        >
            <Stack direction={"row"} sx={{ mb: 2 }} justifyContent={'space-between'} alignItems={'center'}>
                <Typography level="h5">
                    Вопросы теста
                </Typography>
                <Stack direction={"row"} spacing={0.5} alignItems={'center'}>
                    {/* Кнопка Домой */}
                    <IconButton
                        variant="outlined"
                        color="neutral"
                        size="sm"
                        onClick={handleHomeTest}
                    >
                        {<House size={18} />}
                    </IconButton>
                    {/* Кнопка переключения темы */}
                    <IconButton
                        variant="outlined"
                        color="neutral"
                        size="sm"
                        onClick={toggleTheme}
                    >
                        {mode === 'light' ? <Moon size={18} /> : <Sun size={18} />}
                    </IconButton>
                </Stack>
            </Stack>
        

            {/* Статистика прогресса */}
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                <Typography level="body2" sx={{ mb: 1 }}>
                    Прогресс теста
                </Typography>
                <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography level="body3">Отвечено:</Typography>
                        <Typography level="body3" fontWeight="bold">
                            {test.test_questions.filter(q => q.is_answered).length} / {test.actual_questions_count}
                        </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography level="body3">Правильно:</Typography>
                        <Typography level="body3" fontWeight="bold" color="success">
                            {test.test_questions.filter(q => q.user_answer?.is_correct).length}
                        </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography level="body3">Пропущено:</Typography>
                        <Typography level="body3" fontWeight="bold">
                            {test.test_questions.filter(q => q.is_skipped).length}
                        </Typography>
                    </Stack>

                    <Button
                        variant={instantAnswerMode ? "solid" : "outlined"}
                        color={instantAnswerMode ? "primary" : "neutral"}
                        onClick={() => setInstantAnswerMode(!instantAnswerMode)}
                        fullWidth
                        sx={{ mb: 2 }}
                        startDecorator={instantAnswerMode ? <CheckCircle size={18} weight="fill" /> : <Lightning size={18} weight="fill" />}
                    >
                        {instantAnswerMode ? "Режим ответа после" : "Мгновенный ответ"}
                    </Button>
                </Stack>
            </Sheet>

            <Stack spacing={1}>
                {test.test_questions.map((testQuestion, index) => (
                    <motion.div
                        key={testQuestion.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <Button
                            variant={index === currentQuestionIndex ? 'solid' : 'soft'}
                            color={index === currentQuestionIndex ? 'primary' : 'neutral'}
                            onClick={() => onQuestionSelect(index)}
                            fullWidth
                            sx={{
                                justifyContent: 'flex-start',
                                height: 'auto',
                                py: 1.5,
                                textAlign: 'left',
                                ...(showResult && !testQuestion.is_skipped && index !== currentQuestionIndex && testQuestion.user_answer ? (
                                    testQuestion.user_answer.is_correct ? {
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(45, 212, 168, 0.1)' : 'rgba(15, 123, 108, 0.07)',
                                        color: (theme) => theme.palette.mode === 'dark' ? '#2dd4a8' : '#0F7B6C',
                                    } : {
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(190, 70, 70, 0.12)' : 'rgba(168, 50, 50, 0.07)',
                                        color: (theme) => theme.palette.mode === 'dark' ? '#e07070' : '#a83232',
                                    }
                                ) : {}),
                            }}
                        >
                            <Stack spacing={0.5} alignItems="flex-start">
                                <Typography level="body2">
                                    Вопрос {index + 1}
                                </Typography>
                                
                                {/* Статус вопроса */}
                                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                    {testQuestion.user_answer ? (
                                        <Chip
                                            size="sm"
                                            variant="soft"
                                            sx={testQuestion.user_answer.is_correct ? {
                                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(45, 212, 168, 0.15)' : 'rgba(15, 123, 108, 0.1)',
                                                color: (theme) => theme.palette.mode === 'dark' ? '#2dd4a8' : '#0F7B6C',
                                            } : {
                                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(190, 70, 70, 0.18)' : 'rgba(168, 50, 50, 0.1)',
                                                color: (theme) => theme.palette.mode === 'dark' ? '#e07070' : '#a83232',
                                            }}
                                        >
                                            {testQuestion.user_answer.is_correct ? 'Правильно' : 'Ошибка'}
                                        </Chip>
                                    ) : testQuestion.is_answered ? (
                                        <Chip size="sm" color="primary" variant="soft">
                                            Отвечен
                                        </Chip>
                                    ) : null}
                                    {testQuestion.is_skipped && (
                                        <Chip size="sm" color="neutral" variant="soft">
                                            Пропущен
                                        </Chip>
                                    )}
                                </Stack>
                            </Stack>
                        </Button>
                    </motion.div>
                ))}
            </Stack>
        </Sheet>
    );
}

// Вспомогательная функция для определения цвета вопроса
function getQuestionColor(testQuestion, isCurrent) {
    if (isCurrent) return 'primary';
    if (testQuestion.user_answer) {
        return testQuestion.user_answer.is_correct ? 'success' : 'danger';
    }
    if (testQuestion.is_skipped) return 'neutral';
    if (testQuestion.is_answered) return 'primary';
    return 'neutral';
}